// auth-watch.ts — did an anonymous visitor log in (or sign up) after seeing us?
//
// Born 2026-09-15. The dashboard only knew visitors who already bet; nothing on
// the wire said a visitor was logged OUT when they used the widget. `star_gate`'s
// jwt/auth readings are not a login signal: 77 of 552 sessions it called
// anonymous placed a bet in the same session.
//
// This reads the sport app's own `user.Id > 0` (boolean only — the id never
// leaves the page) and sends `auth_change` on a transition. The last state lives
// in sessionStorage, because the host SPA remounts the SDK per navigation and a
// login usually reloads the page.
//
// ⛔ A `false` read early is NOT trusted: the app answers `user.Id 0` for a
// logged-in user until it hydrates (post-mortem 2026-09-12 §10/§11). A false is
// recorded only after AUTH_SETTLE_MS; a true is trusted at once.
//
// ⚠️ 15s is NOT a measured bound on hydration. So every `auth_change` says
// whether the logged-out state was recorded on THIS page load (`same_page`) and
// how far into it the flip came. A hydration flip is always same_page; a login
// that reloads the page never is. Measure the split before trusting same_page flips.

export const AUTH_SETTLE_MS = 15000;
export const AUTH_POLL_MS = 5000;

type Client = {
  sendEvent: (sessionId: string, event: string, suggestionId: string,
    bookNumber?: string | null, meta?: Record<string, unknown>) => unknown;
};
export interface AuthStore { get: () => string | null; set: (v: string) => void }
export interface AuthWatch {
  /** Poll once. Call on an interval. */
  tick: () => void;
  /** The state to stamp on events: a raw true, or a false the watch has settled on. */
  stamp: () => boolean | null;
}

export function makeAuthWatch(
  client: Client,
  read: () => boolean | null,
  store: AuthStore,
  now: () => number = () => performance.now(),
): AuthWatch {
  let zeroSetThisPage = false;
  const safeRead = (): boolean | null => {
    try { return read(); } catch { return null; }
  };
  const tick = (): void => {
    const cur = safeRead();
    if (cur === null) return;
    if (cur === false && now() < AUTH_SETTLE_MS) return;
    const next = cur ? "1" : "0";
    const prev = store.get();
    if (prev === next) return;
    store.set(next);
    const samePage = zeroSetThisPage;
    zeroSetThisPage = next === "0";
    if (prev === null) return;   // first reading this tab: a state, not a change
    client.sendEvent("", "auth_change", "", null, {
      from_logged_in: prev === "1", to_logged_in: cur,
      ms_since_load: Math.round(now()), same_page: samePage,
    });
  };
  const stamp = (): boolean | null => {
    const cur = safeRead();
    if (cur === true) return true;
    if (cur === false) return store.get() === "0" ? false : null;
    return null;
  };
  return { tick, stamp };
}
